// src/pages/Auth/ConfirmEmail.tsx
import { useEffect, useState } from 'react';
import { Result, Button, Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../stores/root-store-context';
import { t } from 'i18next';

type State = 'loading' | 'success' | 'already' | 'error';

function getRawQueryParam(search: string, key: string): string {
  if (!search) return '';
  const escKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // ескейп для RegExp
  const m = search.match(new RegExp(`[?&]${escKey}=([^&#]*)`));
  return m?.[1] ?? '';
}

export default function ConfirmChangedEmail() {
  const [state, setState] = useState<State>('loading');
  const [newEmail, setNewEmail] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const { search } = useLocation();
  const navigate = useNavigate();
  const { applicationStore } = useStore();

  useEffect(() => {
    const oldEmail = getRawQueryParam(search, 'oldEmail');
    const newEmail = getRawQueryParam(search, 'newEmail');
    const code = getRawQueryParam(search, 'code');
    setNewEmail(newEmail);
    if (!oldEmail || !newEmail || !code) {
      setState('error');
      setErrorText('Missing confirmation parameters.');
      return;
    }

    (async () => {
      try {
        await applicationStore.confirmChangedEmail(oldEmail, newEmail, code);
        setState('success');
      } catch (err: any) {
        const msg = err?.response?.data || 'Confirmation failed.';
        setErrorText(typeof msg === 'string' ? msg : 'Confirmation failed.');
        if (msg?.toString().toLowerCase().includes('already')) setState('already');
        else setState('error');
      }
    })();
  }, [search]);

  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>{t('Confirming your email…')}</div>
        </div>
      </div>
    );
  }

  const goLogin = () => navigate('/login');
  const goHome = () => navigate('/');

  if (state === 'success') {
    return (
      <Result
        status="success"
        title={t('Email confirmed!')}
        subTitle={`${t('Your account was confirmed successfully new Email:')} ${newEmail}`}
        extra={[
          <Button type="primary" key="login" onClick={goLogin}>{t('Sign in')}</Button>,
          <Button key="home" onClick={goHome}>{t('Go to Home')}</Button>,
        ]}
      />
    );
  }

  if (state === 'already') {
    return (
      <Result
        status="info"
        title={t('Email already confirmed')}
        subTitle={t('You can sign in to your account.')}
        extra={[
          <Button type="primary" key="login" onClick={goLogin}>{t('Sign in')}</Button>,
          <Button key="home" onClick={goHome}>{t('Go to Home')}</Button>,
        ]}
      />
    );
  }

  return (
    <Result
      status="error"
      title={t('Confirmation failed')}
      subTitle={t(errorText)}
      extra={[
        <Button key="home" onClick={goHome}>{t('Go to Home')}</Button>,
      ]}
    />
  );
}
